/**
 * Destek talebi sözleşmeleri (M4 WP 4.3/4.4). Kaynak: keşif tur 2 F12 (çekmece form alanları, durum kodları, beklemeye alma
 * seçenekleri, SLA formu), tur 1 O19–O23; kararlar `docs/planning/m4-decisions-2026-09-29.md` K-M4-20/21/22. Migration 0058.
 * SLA hesabı ayrı dosyadadır (`ticket-sla.ts`).
 *
 * Talep sahibi bir kişi kaydı DEĞİLDİR: serbest metin (ad soyad, şirket adı, e-posta, telefon) + isteğe bağlı müşteri bağı
 * (şirket/kişi kaydı). E-posta gönderimi M5'e kadar kapalıdır: "yanıt" uygulama içinde talep sahibine görünür olarak
 * işaretlenen mesajdır; hiçbir yerde "e-posta gönderildi" denmez.
 */
import { z } from 'zod';
import { EMAIL_MAX_RECIPIENTS, emailRecipientSchema, emailStatusRefSchema, normalizeEmailRecipients, } from './email.js';
import { localToUtc } from './work-calendar.js';
import { addDaysIso, localDateIso } from './activities.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'saat SS:DD olmalı');
/** Seçenek listesi anahtarı (tenant tanımlı; sunucu üretir: `c_…`) */
export const TICKET_LOOKUP_KEY_RE = /^[a-z][a-z0-9_]{1,39}$/;
const lookupKey = z.string().regex(TICKET_LOOKUP_KEY_RE, 'geçersiz seçenek');
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
// ---------------------------------------------------------------------------------------------------------------------
// Durumlar (K-M4-20)
// ---------------------------------------------------------------------------------------------------------------------
export const TICKET_STATUSES = ['new', 'in_progress', 'on_hold', 'resolved', 'cancelled'];
export const TICKET_STATUS_LABELS = {
    new: 'Açık - Yeni',
    in_progress: 'Açık - Devam ediyor',
    on_hold: 'Açık - Beklemede',
    resolved: 'Kapalı - Çözüldü',
    cancelled: 'Kapalı - İptal',
};
export const TICKET_OPEN_STATUSES = ['new', 'in_progress', 'on_hold'];
export const TICKET_CLOSED_STATUSES = ['resolved', 'cancelled'];
export const isTicketOpen = (s) => TICKET_OPEN_STATUSES.includes(s);
/**
 * İzinli elle geçişler (PM, K-M4-20). `on_hold → on_hold` bekleme sebebini/süresini değiştirir. Otomatik geçişler: ilk görünür
 * yanıt `new → in_progress`; bekleme süresi dolunca `on_hold → in_progress` (worker, tekil). Yeniden açma kapalı → `in_progress`.
 */
export const TICKET_TRANSITIONS = {
    new: ['in_progress', 'on_hold', 'resolved', 'cancelled'],
    in_progress: ['on_hold', 'resolved', 'cancelled'],
    on_hold: ['on_hold', 'in_progress', 'resolved', 'cancelled'],
    resolved: ['in_progress'],
    cancelled: ['in_progress'],
};
export const canTransitionTicket = (from, to) => TICKET_TRANSITIONS[from].includes(to);
/**
 * P-İ1 T3 — müşteri yanıtının talep durumuna etkisi (saf kural; sunucu `applyCustomerReplyWithin` bunu uygular). Kaynak ne olursa
 * olsun (destek e-posta kanalı, talebe bağlı konuşmanın kanalı) aynı kural:
 *  - Olay anı = iletinin gönderilme anı, alınma anını aşamaz (ileri tarihli başlık alınma anına kırpılır).
 *  - Gecikmiş olay: an, talebin SON durum değişikliğinden önceyse durum değişmez (ileti kaydedilir, bildirim gider) — eski ileti
 *    sonradan verilmiş bir kararı (beklemeye alma, çözme) geri almaz.
 *  - `waiting_customer` beklemesindeki talep devama alınır; iç beklemedeki (`internal`) talep beklemede kalır.
 *  - Çözülmüş talep yeniden açılma süresi içindeyse yeniden açılır (döngü artar); süre dışında ya da süre 0 ise açılmaz.
 *  - İptal edilmiş talep müşteri yanıtıyla açılmaz. Yeni / devam eden talepte durum zaten bizdedir.
 * Aynı ileti iki kez gelirse ikinci olay hiçbir şey yapmaz (tekillik ileti kimliğinde; bu fonksiyonun dışında).
 */
export const CUSTOMER_REPLY_ACTIONS = ['resume', 'reopen', 'none'];
export const CUSTOMER_REPLY_REASONS = [
    'waiting_customer',
    'in_window',
    'open',
    'internal_hold',
    'outside_window',
    'reopen_disabled',
    'cancelled',
    'late',
];
const DAY_MS = 86_400_000;
export function customerReplyDecision(i) {
    const at = new Date(Math.min(i.occurredAt.getTime(), i.receivedAt.getTime()));
    if (i.lastStatusAt !== null && at.getTime() < i.lastStatusAt.getTime())
        return { action: 'none', reason: 'late', at };
    switch (i.status) {
        case 'on_hold':
            return i.holdReason === 'waiting_customer'
                ? { action: 'resume', reason: 'waiting_customer', at }
                : { action: 'none', reason: 'internal_hold', at };
        case 'resolved': {
            if (i.reopenWindowDays <= 0)
                return { action: 'none', reason: 'reopen_disabled', at };
            const closed = i.closedAt?.getTime() ?? Number.NEGATIVE_INFINITY;
            return at.getTime() - closed <= i.reopenWindowDays * DAY_MS
                ? { action: 'reopen', reason: 'in_window', at }
                : { action: 'none', reason: 'outside_window', at };
        }
        case 'cancelled':
            return { action: 'none', reason: 'cancelled', at };
        default:
            return { action: 'none', reason: 'open', at };
    }
}
/** Z6 r3 (0400): talebin müşteri kuruluşu kaynağı (`tickets.customer_company_source`; migration ile aynı kapalı liste) */
export const TICKET_CUSTOMER_COMPANY_SOURCES = [
    'direct',
    'contact_at_link',
    'backfill_contact_current',
    'backfill_conflict',
];
/**
 * Portal kuruluş güvenliği (mevcut güvenlik G-1): kuruluş değeri KESİN sayılan kaynaklar. Kuruluşun kayıtlı olması doğrulandığı
 * anlamına gelmez: `backfill_contact_current` (geçişte kişinin o günkü şirketi) ve `backfill_conflict` (belirlenemedi) kesin
 * değildir. Talep yalnız kesin kuruluşunun müşteri işine bağlanır, yalnız o kuruluş için portalda yayınlanır ve portal okuması aynı
 * şartı uygular (sunucu; `ticketCompanyStatus` ile SQL yüklemi aynı listeden türetilir).
 */
export const TICKET_VERIFIED_COMPANY_SOURCES = [
    'direct',
    'contact_at_link',
];
/**
 * Talebin müşteri kuruluşu durumu (bütün yüzeylerde tek anlam):
 * - `verified`: kuruluş var ve kaynağı kesin;
 * - `unverified`: kuruluş kayıtlı ama kaynağı kesin değil (eski kayıt);
 * - `unresolved`: kuruluş belirlenemedi (çelişki) — bireysel müşteri DEĞİLDİR;
 * - `none`: müşteri kuruluşu yok (bireysel müşteri ya da müşterisiz talep).
 */
export const TICKET_COMPANY_STATUSES = ['verified', 'unverified', 'unresolved', 'none'];
/**
 * Portal kuruluş güvenliği (mevcut güvenlik G-1): kesin olmayan / belirlenemeyen eski talebin kuruluşunun AÇIK doğrulaması. Sıradan
 * müşteri değişimi eski talebi kesinleştirmez (migration 0410); yalnız bu işlem — doğru şirket açıkça belirtilir, gerekçe sunucuda
 * zorunludur, önceki/yeni değer denetime yazılır. Talebin müşterisi o şirkete şu an bağlı kişiyse kişi korunur, değilse müşteri şirketin
 * kendisi olur. Doğrulama yayın değildir: açık yayın kapanır, yeniden göstermek ayrı karardır.
 */
export const verifyTicketCustomerCompanySchema = z
    .object({
    companyId: uuid,
    reason: z.string().trim().min(10, 'gerekçe en az 10 karakter').max(500),
})
    .strict();
export function ticketCompanyStatus(source, companyId) {
    if (source === 'backfill_conflict')
        return 'unresolved';
    if (companyId === null)
        return 'none';
    return TICKET_VERIFIED_COMPANY_SOURCES.includes(source ?? '')
        ? 'verified'
        : 'unverified';
}
export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'critical'];
export const TICKET_PRIORITY_LABELS = {
    low: 'Düşük',
    normal: 'Normal',
    high: 'Yüksek',
    critical: 'Kritik',
};
/** Beklemede sebebi (K-M4-20): yalnız müşteri beklemesi çözüm süresini durdurur (K-M4-21). */
export const TICKET_HOLD_REASONS = ['waiting_customer', 'internal'];
export const TICKET_HOLD_REASON_LABELS = {
    waiting_customer: 'Müşteriden bilgi bekleniyor',
    internal: 'İç bekleme',
};
/**
 * E1 (24 Ekim 2026; TI-026): açık talepte topun kimde olduğu. Müşteri = beklemede ve sebep "müşteriden bilgi bekleniyor";
 * diğer her açık durum (yeni, devam ediyor, iç bekleme) = biz. Kapalı (çözüldü/iptal) talepte bekleyen taraf yoktur.
 */
export const TICKET_WAITING_PARTIES = ['us', 'customer'];
export const TICKET_WAITING_PARTY_LABELS = {
    us: 'Bizde',
    customer: 'Müşteride',
};
export const TICKET_WAITING_PARTY_HINTS = {
    us: 'Sıradaki yanıt ya da işlem ekipten bekleniyor.',
    customer: 'Müşteriden bilgi bekleniyor; çözüm süresi durdu.',
};
/**
 * E1: açık talebin SLA durumu (liste süzgeci ve destek kuyrukları AYNI tanım). Hedef aşıldı = güncel döngüde çözüm hedefi ya da
 * (ilk yanıt verilmemişken) ilk yanıt hedefi ihlal kaydı var (destek panosu "SLA aşılmış" sayısıyla aynı kural); hedefe yaklaşıyor
 * = aşılmamış ve sıradaki hedefe `SUPPORT_DUE_SOON_HOURS` saatten az kaldı; SLA yok = talebe sabitlenmiş politika yok.
 */
export const TICKET_SLA_STATES = ['breached', 'due_soon', 'on_track', 'none'];
export const TICKET_SLA_STATE_LABELS = {
    breached: 'Hedef aşıldı',
    due_soon: 'Hedefe yaklaşıyor',
    on_track: 'Hedef içinde',
    none: 'SLA tanımlı değil',
};
export const SUPPORT_DUE_SOON_HOURS = 4;
/** Bekleyen taraf (saf; liste süzgecinin SQL ifadesiyle aynı kural). Kapalı talepte `null`. */
export function ticketWaitingParty(status, holdReason) {
    if (status === 'new' || status === 'in_progress')
        return 'us';
    if (status === 'on_hold')
        return holdReason === 'waiting_customer' ? 'customer' : 'us';
    return null;
}
/** Beklemeye alma seçenekleri (keşif F12: Yarın / Gelecek hafta / Süresiz / Tarih seç). */
export const TICKET_HOLD_OPTIONS = ['tomorrow', 'next_week', 'indefinite', 'date'];
export const TICKET_HOLD_OPTION_LABELS = {
    tomorrow: 'Yarın',
    next_week: 'Gelecek hafta',
    indefinite: 'Süresiz',
    date: 'Tarih seç',
};
/** Bekleme dönüş saati (PM): "Yarın" / "Gelecek hafta" / saatsiz tarih → yerel 09:00 */
export const TICKET_HOLD_RESUME_TIME = '09:00';
function isoParts(iso) {
    return iso.split('-').map(Number);
}
function minutesOfHhmm(t) {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
}
function isoWeekdayOf(iso) {
    const [y, m, d] = isoParts(iso);
    const w = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return w === 0 ? 7 : w;
}
/**
 * Bekleme bitiş anı (tenant saat diliminde, saf): Yarın = ertesi gün 09:00; Gelecek hafta = sonraki Pazartesi 09:00; Tarih seç =
 * seçilen gün (+ saat, yoksa 09:00); Süresiz = `null`. Seçilen an geçmişte ya da şimdi ise hata döner.
 */
export function ticketHoldUntil(option, ctx) {
    if (option === 'indefinite')
        return { until: null };
    const today = localDateIso(ctx.timeZone, ctx.now);
    let day;
    let time = TICKET_HOLD_RESUME_TIME;
    if (option === 'tomorrow')
        day = addDaysIso(today, 1);
    else if (option === 'next_week')
        day = addDaysIso(today, 8 - isoWeekdayOf(today));
    else {
        if (ctx.date === undefined)
            return { error: { field: 'date', message: 'tarih seçin' } };
        day = ctx.date;
        time = ctx.time ?? TICKET_HOLD_RESUME_TIME;
    }
    const [y, m, d] = isoParts(day);
    const until = new Date(localToUtc(ctx.timeZone, y, m, d, minutesOfHhmm(time)));
    if (until.getTime() <= ctx.now.getTime())
        return { error: { field: 'date', message: 'bekleme bitişi gelecekte olmalı' } };
    return { until };
}
/** Mesaj görünürlüğü: `public` talep sahibine görünür yanıt (ilk yanıt sayılır), `internal` iç not (sayılmaz). */
export const TICKET_MESSAGE_VISIBILITIES = ['public', 'internal'];
export const TICKET_MESSAGE_VISIBILITY_LABELS = {
    public: 'Yanıt (talep sahibine görünür)',
    internal: 'İç not',
};
/**
 * Zaman çizelgesindeki mesaj türleri (v3.1 S10 E2): kullanıcının yazdıkları (`public`, `internal`) + destek kanalına bağlı
 * talepte müşterinin e-postayla gelen yanıtı (`customer`; yalnız sistem yazar, ilk yanıt sayılmaz).
 */
export const TICKET_MESSAGE_KINDS = ['public', 'internal', 'customer'];
/** Z6 T-7: müşteri iletisinin geldiği kanal türü (`channels.type`; demo bağlantısına bağlı kanal `demo`) */
export const TICKET_CUSTOMER_MESSAGE_ORIGINS = [
    'email',
    'whatsapp_cloud',
    'simulator',
    'demo',
    'unknown',
];
export const TICKET_MESSAGE_KIND_LABELS = {
    ...TICKET_MESSAGE_VISIBILITY_LABELS,
    customer: 'Müşteri yanıtı',
};
/**
 * Talebin geldiği kanal: uygulama içi form, (genel yayın açıksa) destek talebi web formu (`ticket-forms.ts`) ya da v3.1 S10'da
 * destek e-posta kanalına gelen ileti (`email`; konuşmaya bağlı).
 */
export const TICKET_CHANNELS = ['app', 'web_form', 'email'];
/** Seçenek listeleri (K-M4-22 sekmeleri: Tip, Kaynaklar, Kategoriler, Kapatma sebepleri, İptal sebepleri). Sistem değeri yok. */
export const TICKET_LOOKUP_KINDS = [
    'type',
    'category',
    'source',
    'close_reason',
    'cancel_reason',
];
export const TICKET_LOOKUP_KIND_LABELS = {
    type: 'Tip',
    category: 'Kategori',
    source: 'Kaynak',
    close_reason: 'Kapatma sebebi',
    cancel_reason: 'İptal sebebi',
};
export const ticketLookupSchema = z.object({
    key: z.string(),
    label: z.string(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
});
export const ticketLookupsResponseSchema = z.object({
    type: z.array(ticketLookupSchema),
    category: z.array(ticketLookupSchema),
    source: z.array(ticketLookupSchema),
    close_reason: z.array(ticketLookupSchema),
    cancel_reason: z.array(ticketLookupSchema),
});
export const saveTicketLookupsInputSchema = z
    .object({
    items: z
        .array(z
        .object({
        key: lookupKey.optional(),
        label: z.string().trim().min(1, 'ad gerekli').max(60),
        isActive: z.boolean().default(true),
    })
        .strict())
        .max(100),
})
    .strict();
/** Modül ayarları (module_settings `tickets`.extra; K-M4-20: kapatma/iptal sebebi zorunluluğu tenant ayarıdır). */
/**
 * P-İ1 T3 — yeniden açılma süresi (gün; kuruluş ayarı). Çözülmüş talebe müşteri yanıtı, çözülme anından itibaren bu SÜRE içinde
 * (süre esaslı: `closed_at + N × 24 saat`, UTC anı aritmetiği; takvim günü değil) gelirse talep kendiliğinden yeniden açılır. Süre
 * dışındaki yanıt (ve iptal edilmiş talebe yanıt) talebi AÇMAZ: yanıt talebin zaman çizelgesine müşteri yanıtı olarak düşer ve
 * sorumluya bildirim gider (mevcut ürün kuralı: yeni talep açılmaz). `0` = kendiliğinden yeniden açma kapalı.
 */
export const TICKET_REOPEN_WINDOW_MAX_DAYS = 365;
export const DEFAULT_TICKET_REOPEN_WINDOW_DAYS = 7;
export const ticketSettingsSchema = z
    .object({
    requireCloseReason: z.boolean(),
    requireCancelReason: z.boolean(),
    /** Verilmezse kayıtlı değer korunur (önceki istemciler bu alanı göndermez) */
    reopenWindowDays: z.number().int().min(0).max(TICKET_REOPEN_WINDOW_MAX_DAYS).optional(),
})
    .strict();
export const DEFAULT_TICKET_SETTINGS = {
    requireCloseReason: false,
    requireCancelReason: false,
    reopenWindowDays: DEFAULT_TICKET_REOPEN_WINDOW_DAYS,
};
export const ticketSettingsViewSchema = ticketSettingsSchema.extend({
    reopenWindowDays: z.number().int().min(0).max(TICKET_REOPEN_WINDOW_MAX_DAYS),
    /**
     * Destek talebi web formunun genel yayını (sunucu anahtarı `PUBLIC_TICKET_FORMS`, varsayılan kapalı; K-M3-26 düzeni). Kapalıyken
     * formlar yönetimde tanımlanır/önizlenir ama herkese açık uçlar genel 404 verir.
     */
    webFormPublished: z.boolean(),
    /** E-posta kutusu (SMTP/IMAP) M5 bağımlılığıdır */
    mailboxAvailable: z.literal(false),
});
// ---------------------------------------------------------------------------------------------------------------------
// Telefon (keşif F12: ülke kodu zorunlu — "+90 veya 0090")
// ---------------------------------------------------------------------------------------------------------------------
/** Telefonu E.164 biçimine getirir (`00` öneki `+` olur; boşluk/tire/parantez atılır). Geçersizse `null`. */
export function normalizeTicketPhone(raw) {
    const s = raw.replace(/[\s\-().]/g, '');
    const e = s.startsWith('00') ? `+${s.slice(2)}` : s;
    return /^\+[1-9]\d{6,14}$/.test(e) ? e : null;
}
export const TICKET_PHONE_HINT = 'Numarayı ülke koduyla girin (örn. +90 ya da 0090 ile başlayarak).';
// ---------------------------------------------------------------------------------------------------------------------
// Girdi şemaları
// ---------------------------------------------------------------------------------------------------------------------
export const ticketAssigneeSchema = z.object({ type: z.enum(['user', 'dept']), id: uuid });
const email = z
    .string()
    .trim()
    .max(254)
    .transform((s) => (s === '' ? null : s))
    .nullable()
    .refine((s) => s === null || z.email().safeParse(s).success, 'geçerli bir e-posta girin');
export const createTicketInputSchema = z
    .object({
    requesterName: z.string().trim().min(1, 'ad soyad gerekli').max(200),
    requesterCompany: optText(200).optional(),
    requesterEmail: email.optional(),
    /** Ülke kodlu telefon; sunucu `normalizeTicketPhone` ile doğrular */
    requesterPhone: optText(40).optional(),
    /** Müşteri (şirket ya da kişi kaydı) */
    relatedRecordId: uuid.nullable().optional(),
    subject: z.string().trim().min(1, 'konu gerekli').max(300),
    details: z.string().trim().min(1, 'detaylar gerekli').max(20000),
    priority: z.enum(TICKET_PRIORITIES),
    typeKey: lookupKey.nullable().optional(),
    categoryKey: lookupKey.nullable().optional(),
    sourceKey: lookupKey.nullable().optional(),
    assignees: z.array(ticketAssigneeSchema).min(1, 'en az bir görevli gerekli').max(20),
    ownerId: uuid.optional(),
    /** Tekrar güvenliği: aynı belirteçle ikinci istek yeni kayıt üretmez */
    clientToken: uuid.optional(),
    customValues: z.record(z.string(), z.unknown()).prefault({}),
})
    .strict();
/**
 * Güncelleme (If-Match). Öncelik, tip ya da SLA politikası değişikliği `changeReason` ister ve denetime yazılır (K-M4-21);
 * önceki ihlal kaydı silinmez.
 */
export const updateTicketInputSchema = z
    .object({
    requesterName: z.string().trim().min(1, 'ad soyad gerekli').max(200).optional(),
    requesterCompany: optText(200).optional(),
    requesterEmail: email.optional(),
    requesterPhone: optText(40).optional(),
    relatedRecordId: uuid.nullable().optional(),
    subject: z.string().trim().min(1, 'konu gerekli').max(300).optional(),
    details: z.string().trim().min(1, 'detaylar gerekli').max(20000).optional(),
    priority: z.enum(TICKET_PRIORITIES).optional(),
    typeKey: lookupKey.nullable().optional(),
    categoryKey: lookupKey.nullable().optional(),
    sourceKey: lookupKey.nullable().optional(),
    assignees: z.array(ticketAssigneeSchema).min(1, 'en az bir görevli gerekli').max(20).optional(),
    ownerId: uuid.optional(),
    /** Talebin SLA politikası (null = SLA'sız). Politikanın GÜNCEL sürümü sabitlenir. */
    slaPolicyId: uuid.nullable().optional(),
    changeReason: z.string().trim().min(3, 'gerekçe en az 3 karakter').max(500).optional(),
    customValues: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const ticketStatusActionSchema = z.discriminatedUnion('action', [
    /** "İşleme al": Yeni → Devam ediyor */
    z.object({ action: z.literal('start') }).strict(),
    z
        .object({
        action: z.literal('hold'),
        reason: z.enum(TICKET_HOLD_REASONS),
        option: z.enum(TICKET_HOLD_OPTIONS),
        date: isoDate.optional(),
        time: hhmm.optional(),
    })
        .strict(),
    /** "Beklemeden çıkar" */
    z.object({ action: z.literal('unhold') }).strict(),
    z
        .object({
        action: z.literal('resolve'),
        reasonKey: lookupKey.nullable().optional(),
        detail: optText(2000).optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal('cancel'),
        reasonKey: lookupKey.nullable().optional(),
        detail: optText(2000).optional(),
    })
        .strict(),
    z.object({ action: z.literal('reopen') }).strict(),
]);
export const TICKET_ACTION_LABELS = {
    start: 'İşleme al',
    hold: 'Beklemeye al',
    unhold: 'Beklemeden çıkar',
    resolve: 'Çözüldü',
    cancel: 'İptal et',
    reopen: 'Yeniden aç',
};
export const ticketMessageInputSchema = z
    .object({
    visibility: z.enum(TICKET_MESSAGE_VISIBILITIES),
    body: z.string().trim().min(1, 'mesaj gerekli').max(20000),
    clientToken: uuid.optional(),
    /**
     * M5 WP 5.4: görünür yanıtı talep sahibine e-posta ile de gönder (şablon `destek-yaniti`). E-posta gönderimi kapalıysa
     * yanıt KAYDEDİLMEZ (409, açık gerekçe) — "gönderildi" sanılmasın; kullanıcı seçeneği kaldırıp yeniden gönderir.
     */
    emailRequester: z.boolean().optional(),
    /**
     * M6-H1: e-postanın kopya (Cc) alıcıları. Yalnız `emailRequester` ile birlikte anlamlıdır; Kime = talep sahibi olduğundan
     * Cc en çok tavan - 1 adrestir (Kime + Cc toplamı tek tavanı aşamaz). Bcc yoktur.
     */
    cc: z
        .array(emailRecipientSchema)
        .max(EMAIL_MAX_RECIPIENTS - 1)
        .optional(),
})
    .strict()
    .refine((v) => v.emailRequester !== true || v.visibility === 'public', {
    message: 'Yalnız talep sahibine görünür yanıt e-postayla gönderilebilir',
    path: ['emailRequester'],
})
    .refine((v) => v.cc === undefined || v.cc.length === 0 || v.emailRequester === true, {
    message: 'Kopya (Cc) yalnız yanıt talep sahibine e-postayla gönderilirken eklenebilir',
    path: ['cc'],
})
    .refine((v) => v.cc === undefined || normalizeEmailRecipients([], v.cc).total <= EMAIL_MAX_RECIPIENTS - 1, {
    message: `Kime ve Cc toplamı en çok ${String(EMAIL_MAX_RECIPIENTS)} alıcı olabilir`,
    path: ['cc'],
});
// ---------------------------------------------------------------------------------------------------------------------
// Çıktı şemaları
// ---------------------------------------------------------------------------------------------------------------------
export const ticketMessageSchema = z.object({
    id: z.string(),
    visibility: z.enum(TICKET_MESSAGE_KINDS),
    body: z.string(),
    authorId: z.string().nullable(),
    authorName: z.string().nullable(),
    createdAt: z.string(),
    /** Bu mesaj talebin ilk yanıtı olarak sayıldı */
    isFirstResponse: z.boolean(),
    /** M5 WP 5.4: yanıt talep sahibine e-postayla gönderildiyse gönderim durumu (SMTP kabulü okundu/teslim değildir) */
    email: emailStatusRefSchema.nullable().optional(),
    /**
     * Z6 T-7: `customer` iletisinin geldiği kanal — kaynak konuşma iletisinden (talep iletisinin istemci belirteci = konuşma ileti
     * kimliği) okunur; kaynak bulunamazsa `unknown`. Ekip mesajında yok. Ad üretilmez: müşteri iletisinin yazarı kullanıcı değildir.
     */
    customerOrigin: z.enum(TICKET_CUSTOMER_MESSAGE_ORIGINS).nullable().optional(),
});
/**
 * Z6 T-7: durum tarihçesi satırının kaynağı. `user`: kullanıcı işlemi · `system`: bekleme süresi doldu (otomatik) · `web_form` /
 * `email`: talep herkese açık web formundan ya da destek e-posta kanalından açıldı · `customer`: müşterinin gelen iletisi talebi
 * devama aldı ya da yeniden açtı · `unknown`: aktörü artık bulunmayan satır (silinmiş kullanıcı) — sistem ya da müşteri SAYILMAZ.
 */
export const TICKET_STATUS_EVENT_SOURCES = [
    'user',
    'system',
    'web_form',
    'email',
    'customer',
    'unknown',
];
export const ticketStatusEventSchema = z.object({
    at: z.string(),
    status: z.enum(TICKET_STATUSES),
    holdReason: z.enum(TICKET_HOLD_REASONS).nullable(),
    holdUntil: z.string().nullable(),
    cycle: z.number().int(),
    /** `TICKET_STATUS_EVENT_SOURCES` */
    source: z.enum(TICKET_STATUS_EVENT_SOURCES),
    actorName: z.string().nullable(),
});
/**
 * Z6 T-7: işlem döngüsü (`resolutionCycle`, SLA çözüm sayacının döngüsü) ilk açılışta 1'dir, her yeniden açılışta bir artar.
 * Yeniden açılma SAYISI tarihçedeki olaylardan türetilir: döngüsü bir önceki satırdan büyük olan her satır bir yeniden açılıştır.
 */
export function ticketReopenCount(history) {
    let n = 0;
    for (let i = 1; i < history.length; i += 1) {
        const cur = history[i];
        const prev = history[i - 1];
        if (cur !== undefined && prev !== undefined && cur.cycle > prev.cycle)
            n += 1;
    }
    return n;
}
export const SLA_METRICS = ['first_response', 'resolution'];
export const SLA_METRIC_LABELS = {
    first_response: 'Başlama hedefi (ilk yanıt)',
    resolution: 'Sonuçlandırma hedefi (çözüm)',
};
export const ticketSlaViewSchema = z.object({
    /** false: talebin SLA'sı tanımsız (müşteriye atanmış ya da varsayılan politika yok) — sıfır/başarı üretilmez */
    defined: z.boolean(),
    policy: z.object({ id: z.string(), name: z.string(), version: z.number().int() }).nullable(),
    useWorkingHours: z.boolean().nullable(),
    /**
     * K-M7-50.5: hesaplama anında (`computedAt`) çalışma takvimine göre MESAİ DIŞI mı. Yalnız çalışma saatine göre sayılan
     * politikada dolu; 7/24 politikada ve SLA tanımsızken `null`. Arayüz "Mesai başlangıcı bekleniyor" etiketini bu alandan
     * gösterir (çıkarım yapmaz).
     */
    outsideWorkingHours: z.boolean().nullable(),
    firstResponse: z
        .object({
        targetMinutes: z.number().int(),
        elapsedMinutes: z.number().int(),
        /** hedef − geçen (negatifse aşıldı) */
        remainingMinutes: z.number().int(),
        state: z.enum(['running', 'stopped', 'met', 'missed']),
        breached: z.boolean(),
        respondedAt: z.string().nullable(),
        /** Sayaç işliyorsa hedef anı (tahmin: bundan sonra duraklama olmazsa) */
        dueAt: z.string().nullable(),
    })
        .nullable(),
    resolution: z
        .object({
        targetMinutes: z.number().int(),
        elapsedMinutes: z.number().int(),
        remainingMinutes: z.number().int(),
        state: z.enum(['running', 'paused', 'stopped']),
        breached: z.boolean(),
        dueAt: z.string().nullable(),
        cycle: z.number().int(),
        pausedSince: z.string().nullable(),
        /** Müşteri beklemesinde geçen (sayılmayan) süre */
        pausedMinutes: z.number().int(),
    })
        .nullable(),
    breaches: z.array(z.object({ metric: z.enum(SLA_METRICS), cycle: z.number().int(), detectedAt: z.string() })),
    computedAt: z.string(),
});
const refSchema = z.object({ id: z.string(), type: z.string(), title: z.string() });
export const ticketProfileSchema = z.object({
    kind: z.literal('ticket'),
    subject: z.string(),
    details: z.string(),
    priority: z.enum(TICKET_PRIORITIES),
    status: z.enum(TICKET_STATUSES),
    holdReason: z.enum(TICKET_HOLD_REASONS).nullable(),
    holdUntil: z.string().nullable(),
    typeKey: z.string().nullable(),
    categoryKey: z.string().nullable(),
    sourceKey: z.string().nullable(),
    requester: z.object({
        name: z.string(),
        company: z.string().nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
    }),
    /** Müşteri kaydı (yalnız çağırana görünürse) */
    related: refSchema.nullable(),
    /** Bağlı müşteri kaydı var ama çağıran göremiyor */
    relatedHidden: z.boolean(),
    /**
     * Z6 T-1: müşteri kuruluşu — talebin kanonik görüntüsü (açılırken / müşterisi değişirken: müşteri şirketse kendisi, kişiyse o anki
     * şirketi; kişinin sonraki şirket değişikliği yansımaz). Yalnız çağırana görünürse; yoksa bireysel müşteri ya da müşterisiz.
     */
    customerCompany: refSchema.nullable(),
    /** Müşteri kuruluşu var ama çağıran göremiyor */
    customerCompanyHidden: z.boolean(),
    /**
     * Z6 r3 (0400): müşteri kuruluşu değerinin kaynağı — `direct` (müşteri şirketin kendisi), `contact_at_link` (kişiye bağlanma anında
     * kişinin şirketi), `backfill_contact_current` (0390 geçişinde kişinin o günkü şirketi; talep açıldığı andaki şirket olduğu kesin
     * değil), `backfill_conflict` (kişinin şirketi talepten sonra değişmiş; kuruluş belirlenemedi, boş). null: müşterisiz ya da
     * müşteri kişi/şirket değil. Önceki sürüm alanı göndermez.
     */
    customerCompanySource: z.enum(TICKET_CUSTOMER_COMPANY_SOURCES).nullable().optional(),
    closeReasonKey: z.string().nullable(),
    closeReasonDetail: z.string().nullable(),
    cancelReasonKey: z.string().nullable(),
    cancelReasonDetail: z.string().nullable(),
    closedAt: z.string().nullable(),
    closedBy: z.string().nullable(),
    resolutionCycle: z.number().int(),
    channel: z.enum(TICKET_CHANNELS),
    /** v3.1 S10: talebin bağlı olduğu konuşma (yalnız çağırana görünürse) */
    conversationId: z.string().nullable().optional(),
    assignees: z.array(ticketAssigneeSchema),
    firstResponseAt: z.string().nullable(),
    sla: ticketSlaViewSchema,
    messages: z.array(ticketMessageSchema),
    /** Mesaj sayısı üst sınırı aşıldı (en yeni N gösterilir) */
    messagesTruncated: z.boolean(),
    history: z.array(ticketStatusEventSchema),
});
export const createTicketResponseSchema = z.object({
    id: z.string(),
    /** Aynı `clientToken` ile önceki istek bulundu; yeni kayıt üretilmedi */
    replayed: z.boolean(),
});
// ---------------------------------------------------------------------------------------------------------------------
// KPI panosu (keşif O19: filtreler, anlık, periyot, grafikler)
// ---------------------------------------------------------------------------------------------------------------------
export const SUPPORT_OVERVIEW_MAX_DAYS = 366;
export const supportOverviewQuerySchema = z
    .object({
    from: isoDate,
    to: isoDate,
    /** Yalnız bana atanmış talepler */
    mine: z.enum(['0', '1']).optional(),
})
    .strict()
    .refine((q) => q.to >= q.from, { message: 'bitiş başlangıçtan önce olamaz', path: ['to'] });
const countsByStatus = z.object({
    new: z.number().int(),
    in_progress: z.number().int(),
    on_hold: z.number().int(),
    resolved: z.number().int(),
    cancelled: z.number().int(),
});
const countsByPriority = z.object({
    low: z.number().int(),
    normal: z.number().int(),
    high: z.number().int(),
    critical: z.number().int(),
});
export const supportOverviewResponseSchema = z.object({
    timeZone: z.string(),
    from: z.string(),
    to: z.string(),
    /** Anlık durum (dönemden bağımsız; yalnız görünür talepler) */
    snapshot: z.object({
        open: z.number().int(),
        byStatus: countsByStatus,
        byPriority: countsByPriority,
        onHoldCustomer: z.number().int(),
        onHoldInternal: z.number().int(),
        /** Açık ve SLA'sı aşılmış (ilk yanıt ya da çözüm; güncel döngü) */
        openBreached: z.number().int(),
        /** Açık ve SLA'sı tanımsız */
        openWithoutSla: z.number().int(),
    }),
    /** Dönem (oluşturma / kapanma / ihlal tespiti tarihleri tenant saat diliminde dönem içinde) */
    period: z.object({
        created: z.number().int(),
        resolved: z.number().int(),
        cancelled: z.number().int(),
        createdByStatus: countsByStatus,
        createdByPriority: countsByPriority,
        /** Dönemde ihlal tespit edilen talep sayısı (metrik başına, talep başına bir kez) */
        breachedFirstResponse: z.number().int(),
        breachedResolution: z.number().int(),
        /** Dönemde oluşturulup ilk yanıt alan talepler: oluşturma → ilk görünür yanıt (takvim süresi, dakika) */
        avgFirstResponseMinutes: z.number().nullable(),
        firstResponseSamples: z.number().int(),
        /** Dönemde çözülen talepler: oluşturma → çözüm (takvim süresi, dakika) */
        avgResolutionMinutes: z.number().nullable(),
        resolutionSamples: z.number().int(),
    }),
    /** Günlük seri (dönem ≤ 92 gün ise; değilse boş) */
    daily: z.array(z.object({ date: z.string(), created: z.number().int(), resolved: z.number().int() })),
});
//# sourceMappingURL=tickets.js.map